import { User } from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit as firestoreLimit,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCompanyDomainFromEmail, generateCompanyId } from './userService';

// Types for Firestore documents
export interface FirestoreUser {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  companyId: string;
  role: 'user' | 'admin' | 'super_admin';
  createdAt: any;
  updatedAt: any;
  lastLogin?: string;
  status?: 'active' | 'inactive';
}

export interface FirestoreCompany {
  id: string;
  name: string;
  domain: string;
  creditBalance: number;
  createdAt: any;
  updatedAt: any;
}

export interface FirestoreCreditTransaction {
  id: string;
  companyId: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  type: 'usage' | 'adjustment';
  description: string;
  createdBy: string;
  createdAt: any;
  serviceType?: string;
  requestId?: string;
}

/**
 * Check if user exists by email
 */
export const getUserByEmailAddress = async (email: string): Promise<FirestoreUser | null> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const userDoc = snapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() } as FirestoreUser;
  } catch (error) {
    console.error('Error getting user by email:', error);
    return null;
  }
};

/**
 * Get or create user and company from Firebase Auth user
 */
export const ensureUserAndCompanyExist = async (user: User) => {
  if (!user.email) {
    throw new Error('User email is required');
  }

  const domain = getCompanyDomainFromEmail(user.email);
  const companyId = generateCompanyId(domain);
  
  try {
    // Check if user exists
    let userDoc = await getUserByEmailAddress(user.email);
    
    // Create or update user document
    const userRef = doc(db, 'users', user.uid);
    const userData: FirestoreUser = {
      id: user.uid,
      email: user.email,
      name: user.displayName || user.email.split('@')[0],
      avatarUrl: user.photoURL || undefined,
      companyId: companyId,
      role: userDoc?.role || 'user',
      createdAt: userDoc?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(userRef, userData, { merge: true });
    
    // Check if company exists
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      // Create company document
      const companyData: FirestoreCompany = {
        id: companyId,
        name: domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1),
        domain: domain,
        creditBalance: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(companyRef, companyData);
    }
    
    return {
      user: userData,
      companyId: companyId
    };
  } catch (error) {
    console.error('Error ensuring user and company exist:', error);
    throw error;
  }
};

/**
 * Get company by ID
 */
export const getCompanyById = async (companyId: string): Promise<FirestoreCompany | null> => {
  try {
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      return null;
    }
    
    return { id: companySnap.id, ...companySnap.data() } as FirestoreCompany;
  } catch (error) {
    console.error('Error getting company:', error);
    return null;
  }
};

/**
 * Get company credit balance
 */
export const getCompanyCredits = async (companyId: string): Promise<number> => {
  const company = await getCompanyById(companyId);
  return company?.creditBalance || 0;
};

/**
 * Consume credits for a service request
 */
export const consumeCompanyCredits = async (
  companyId: string,
  amount: number,
  userId: string,
  serviceType: string,
  requestId?: string
) => {
  return await runTransaction(db, async (transaction) => {
    // Get current company balance
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await transaction.get(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company not found');
    }
    
    const currentBalance = companySnap.data().creditBalance || 0;
    
    if (currentBalance < amount) {
      throw new Error('Insufficient credits');
    }
    
    const balanceBefore = currentBalance;
    const balanceAfter = currentBalance - amount;
    
    // Update company balance
    transaction.update(companyRef, {
      creditBalance: balanceAfter,
      updatedAt: serverTimestamp()
    });
    
    // Create transaction record
    const transactionData: Omit<FirestoreCreditTransaction, 'id'> = {
      companyId,
      amount: -amount, // Negative for consumption
      balanceBefore,
      balanceAfter,
      type: 'usage',
      description: `${serviceType} service - ${amount} credits`,
      createdBy: userId,
      createdAt: serverTimestamp(),
      serviceType,
      requestId: requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    const transactionRef = doc(collection(db, 'creditTransactions'));
    transaction.set(transactionRef, transactionData);
    
    return {
      transactionId: transactionRef.id,
      balanceAfter
    };
  });
};

/**
 * Refund credits when a service fails after deduction
 */
export const refundCompanyCredits = async (
  companyId: string,
  amount: number,
  userId: string,
  originalServiceType: string,
  originalRequestId: string,
  failureReason: string
): Promise<{ success: boolean; newBalance: number; error?: string }> => {
  try {
    return await runTransaction(db, async (transaction) => {
      // Get current company balance
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await transaction.get(companyRef);
      
      if (!companySnap.exists()) {
        throw new Error('Company not found');
      }
      
      const currentBalance = companySnap.data().creditBalance || 0;
      const balanceBefore = currentBalance;
      const balanceAfter = currentBalance + amount; // Add credits back
      
      // Update company balance
      transaction.update(companyRef, {
        creditBalance: balanceAfter,
        updatedAt: serverTimestamp()
      });
      
      // Create refund transaction record
      const transactionData: Omit<FirestoreCreditTransaction, 'id'> = {
        companyId,
        amount, // Positive for refund
        balanceBefore,
        balanceAfter,
        type: 'adjustment',
        description: `Refund: ${originalServiceType} service failed - ${amount} credits refunded (Reason: ${failureReason})`,
        createdBy: userId,
        createdAt: serverTimestamp(),
        serviceType: originalServiceType,
        requestId: `refund_${originalRequestId}`
      };
      
      const transactionRef = doc(collection(db, 'creditTransactions'));
      transaction.set(transactionRef, transactionData);
      
      return {
        success: true,
        newBalance: balanceAfter
      };
    });
  } catch (error: any) {
    console.error('Error refunding company credits:', error);
    return {
      success: false,
      newBalance: 0,
      error: error.message || 'Failed to refund credits'
    };
  }
};

/**
 * Get credit transaction history for a company
 */
export const getCompanyCreditTransactions = async (companyId: string, limit = 50): Promise<FirestoreCreditTransaction[]> => {
  try {
    const transactionsRef = collection(db, 'creditTransactions');
    const q = query(
      transactionsRef,
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limit)
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FirestoreCreditTransaction[];
  } catch (error) {
    console.error('Error getting credit transactions:', error);
    return [];
  }
};

/**
 * Get all companies (Super Admin only)
 */
export const getAllCompanies = async (): Promise<FirestoreCompany[]> => {
  try {
    const companiesRef = collection(db, 'companies');
    const snapshot = await getDocs(companiesRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FirestoreCompany[];
  } catch (error) {
    console.error('Error getting all companies:', error);
    return [];
  }
};

/**
 * Get all users (Super Admin only)
 */
export const getAllUsers = async (): Promise<FirestoreUser[]> => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FirestoreUser[];
  } catch (error) {
    console.error('Error getting all users:', error);
    return [];
  }
};

/**
 * Get user count for a company
 */
export const getCompanyUserCount = async (companyId: string): Promise<number> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    
    return snapshot.size;
  } catch (error) {
    console.error('Error getting company user count:', error);
    return 0;
  }
};

/**
 * Add or deduct credits for a company (Super Admin only)
 */
export const adjustCompanyCredits = async (
  companyId: string,
  amount: number,
  userId: string,
  description: string
): Promise<{ success: boolean; newBalance: number; error?: string }> => {
  try {
    return await runTransaction(db, async (transaction) => {
      // Get current company balance
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await transaction.get(companyRef);
      
      if (!companySnap.exists()) {
        throw new Error('Company not found');
      }
      
      const currentBalance = companySnap.data().creditBalance || 0;
      const balanceBefore = currentBalance;
      const balanceAfter = currentBalance + amount; // amount can be negative for deduction
      
      // Prevent negative balances for deductions
      if (amount < 0 && balanceAfter < 0) {
        throw new Error('Insufficient credits for deduction');
      }
      
      // Update company balance
      transaction.update(companyRef, {
        creditBalance: balanceAfter,
        updatedAt: serverTimestamp()
      });
      
      // Create transaction record
      const transactionData: Omit<FirestoreCreditTransaction, 'id'> = {
        companyId,
        amount,
        balanceBefore,
        balanceAfter,
        type: amount > 0 ? 'adjustment' : 'adjustment',
        description,
        createdBy: userId,
        createdAt: serverTimestamp()
      };
      
      const transactionRef = doc(collection(db, 'creditTransactions'));
      transaction.set(transactionRef, transactionData);
      
      return {
        success: true,
        newBalance: balanceAfter
      };
    });
  } catch (error: any) {
    console.error('Error adjusting company credits:', error);
    return {
      success: false,
      newBalance: 0,
      error: error.message || 'Failed to adjust credits'
    };
  }
};
